import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ADD_MESSAGE,
  UPDATE_MESSAGE,
  ADD_THREAD,
  ADD_HANDOFF,
  UNREAD_COUNT,
  GET_MESSAGES,
  GET_ACTION_LOGS
} from 'src/actions/chatActions';
import { LAST_SEEN } from 'src/actions/leadActions';
import NotificationSound from 'src/assets/sounds/facebookchat.mp3';
import NewConversationSound from 'src/assets/sounds/notification-01.mp3';
import _ from 'lodash';
import { addNotifications } from './actions/notificationsActions';
import {
  initiateSocket,
  disconnectSocket,
  subscribeToChat
} from './services/socket';

const WebSocketContext = createContext(null);

export { WebSocketContext };

export default ({ children, room }) => {
  const [socket, setSocket] = useState(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('studio');
  const location = useLocation();
  const user = useSelector(state => state.account.user);
  const threads = useSelector(state => state.chat.threads.byKey);
  const handoffs = useSelector(state => state.chat.handoffs.byKey);
  const audioRef = useRef(null);
  const newConversationAudioRef = useRef(null);
  const createNotificationRef = useRef(null);

  // Initialize audio once
  useEffect(() => {
    audioRef.current = new Audio(NotificationSound);
    newConversationAudioRef.current = new Audio(NewConversationSound);
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (newConversationAudioRef.current) {
        newConversationAudioRef.current.pause();
        newConversationAudioRef.current = null;
      }
    };
  }, []);

  // Create stable debounced notification function
  useEffect(() => {
    createNotificationRef.current = _.debounce(
      async (msg, useNewConversationSound = false) => {
        if (document.visibilityState === 'hidden') {
          dispatch(addNotifications());
          const soundToPlay = useNewConversationSound
            ? newConversationAudioRef.current
            : audioRef.current;
          if (soundToPlay) {
            await soundToPlay.play().catch(err => {
              console.warn('Failed to play notification sound:', err);
            });
          }
          document.title = msg;
        }
      },
      1000
    );

    return () => {
      if (createNotificationRef.current) {
        createNotificationRef.current.cancel();
      }
    };
  }, [dispatch]);

  const userRef = useRef(user);
  const threadsRef = useRef(threads);
  const handoffsRef = useRef(handoffs);
  const locationRef = useRef(location);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  useEffect(() => {
    handoffsRef.current = handoffs;
  }, [handoffs]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Helper function to determine if notification should be shown
  const shouldShowNotification = useCallback((eventType, data) => {
    const currentPath = locationRef.current?.pathname || '';
    const isLivechatPage = currentPath.includes('/livechat/');
    const isSupportPage = currentPath.includes('/support/');
    const currentUser = userRef.current;

    // If on livechat page
    if (isLivechatPage) {
      if (eventType === 'newThread') {
        // Show notification only if thread is not paused
        return !data.thread?.paused;
      }
      if (eventType === 'newMessage') {
        // Show notification only if message belongs to non-paused thread
        const thread = threadsRef.current?.[data.message?.thread_id];
        return thread && !thread.paused;
      }
      if (eventType === 'newHandoff') {
        // No notifications for handoffs on livechat page
        return false;
      }
    }

    // If on support page
    if (isSupportPage) {
      if (eventType === 'newThread') {
        // No notifications for new threads on support page
        return false;
      }
      if (eventType === 'newMessage') {
        // Show notification only if message belongs to paused thread
        // AND handoff is unassigned or assigned to current user
        const thread = data.thread || threadsRef.current?.[data.message?.thread_id];
        if (!thread || !thread.paused) {
          return false;
        }

        // Find handoff for this thread
        // An explicit null means the server found no open ticket. Only fall
        // back to cached data for events from servers without this context.
        const handoff = Object.prototype.hasOwnProperty.call(data, 'handoff')
          ? data.handoff
          : Object.values(handoffsRef.current || {}).find(
              h => h.thread_id === data.message?.thread_id && !h.resolved_at
            );

        // If no handoff found, don't show notification
        if (!handoff) {
          return false;
        }

        // Show if unassigned (assignee is 'none', null, undefined, or empty string)
        // OR assigned to current user
        const isUnassigned =
          !handoff.assignee ||
          handoff.assignee === 'none' ||
          handoff.assignee === '';
        const isAssignedToMe = handoff.assignee === currentUser?.email;

        return isUnassigned || isAssignedToMe;
      }
      if (eventType === 'newHandoff') {
        // Show notifications for new handoffs on support page
        return true;
      }
    }

    // Default: don't show notification if not on livechat or support page
    return false;
  }, []);

  const setupSocketListeners = useCallback(() => {
    subscribeToChat(async (err, data) => {
      if (err) {
        console.error('Socket error:', err);
        return;
      }

      switch (data.type) {
        case 'actionLogs':
          dispatch({
            type: GET_ACTION_LOGS,
            payload: { logs: data.logs }
          });
          break;

        case 'newMessage':
          dispatch({
            type: ADD_MESSAGE,
            payload: { message: data.message }
          });

          // Only show notification based on current page and context
          if (shouldShowNotification('newMessage', data)) {
            await createNotificationRef.current?.('New Messages');
          }
          break;

        case 'updateMessage':
          dispatch({
            type: UPDATE_MESSAGE,
            payload: { message: data.message }
          });
          break;

        case 'unreadCount':
          dispatch({
            type: UNREAD_COUNT,
            payload: data.unreadCount
          });
          dispatch({
            type: LAST_SEEN,
            payload: data.unreadCount
          });
          break;

        case 'newThread':
          // Check if thread already exists in Redux before showing notification
          const existingThread = threadsRef.current?.[data.thread.id];

          dispatch({
            type: ADD_THREAD,
            payload: { thread: data.thread }
          });

          // Only show notification if this is a truly new thread and should be shown based on page context
          // Use new conversation sound for new threads in livechat
          if (!existingThread && shouldShowNotification('newThread', data)) {
            await createNotificationRef.current?.('New Conversation!', true);
          }
          break;

        case 'newHandoff':
          dispatch({
            type: ADD_HANDOFF,
            payload: { handoff: data.handoff }
          });

          // Only show notification based on current page context
          if (shouldShowNotification('newHandoff', data)) {
            await createNotificationRef.current?.('New Ticket!');
          }
          break;

        case 'updateHandoff':
          dispatch({
            type: ADD_HANDOFF,
            payload: { handoff: data.handoff }
          });
          break;

        case 'messageHistory':
          dispatch({
            type: GET_MESSAGES,
            payload: {
              threadId: data.id,
              messages: data.messages
            }
          });
          dispatch({
            type: ADD_THREAD,
            payload: { thread: _.omit(data, ['messages']) }
          });
          break;

        case 'handoffAssigned':
          // Show notification only to the assigned agent
          const currentUser = userRef.current;
          if (currentUser && data.assignee === currentUser.email) {
            enqueueSnackbar(
              t('Support.notifications.handoffAssigned', {
                ticketNumber: data.handoff.ticket_number
              }),
              {
                variant: 'info'
              }
            );
            // Play new conversation sound for handoff assignments
            if (
              document.visibilityState === 'hidden' &&
              newConversationAudioRef.current
            ) {
              await newConversationAudioRef.current.play().catch(err => {
                console.warn('Failed to play notification sound:', err);
              });
            }
          }
          break;

        default:
          console.warn('Unsupported event type:', data.type);
          break;
      }
    });
  }, [dispatch, enqueueSnackbar, t]);

  const connectSocket = useCallback(() => {
    if (!room) {
      console.warn('WebSocket: No room provided for connection');
      return null;
    }

    try {
      const newSocket = initiateSocket(room);
      setSocket(newSocket);
      return newSocket;
    } catch (error) {
      console.error('Failed to connect socket:', error);
      return null;
    }
  }, [room]);

  const waitForSocketConnection = useCallback(socketInstance => {
    if (!socketInstance) {
      return Promise.resolve(null);
    }

    if (socketInstance.connected) {
      return Promise.resolve(socketInstance);
    }

    return new Promise(resolve => {
      let timeoutId;
      let handleConnect;
      let handleReconnectFailed;

      handleConnect = () => {
        clearTimeout(timeoutId);
        socketInstance.off('reconnect_failed', handleReconnectFailed);
        resolve(socketInstance);
      };

      handleReconnectFailed = () => {
        clearTimeout(timeoutId);
        socketInstance.off('connect', handleConnect);
        resolve(null);
      };

      timeoutId = setTimeout(() => {
        socketInstance.off('connect', handleConnect);
        socketInstance.off('reconnect_failed', handleReconnectFailed);
        resolve(null);
      }, 30000);

      socketInstance.once('connect', handleConnect);
      socketInstance.once('reconnect_failed', handleReconnectFailed);
    });
  }, []);

  const reconnectSocket = useCallback(async () => {
    disconnectSocket();
    const newSocket = connectSocket();
    if (newSocket) {
      setupSocketListeners();
      return waitForSocketConnection(newSocket);
    }
    return null;
  }, [connectSocket, setupSocketListeners, waitForSocketConnection]);

  useEffect(() => {
    if (!room) return;

    const ws = connectSocket();
    if (ws) {
      setupSocketListeners();
    }

    return () => {
      disconnectSocket();
    };
  }, [room, connectSocket, setupSocketListeners]);

  const contextValue = useMemo(
    () => ({
      socket,
      reconnectSocket
    }),
    [socket, reconnectSocket]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
